import React, { useState, useMemo,useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';

function RecordPaymentForm() {
    const [accountNumber, setAccountNumber] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [customerData, setCustomerData] = useState(null); 
    const [amountToPay, setAmountToPay] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [transactionType, setTransactionType] = useState('PAYMENT'); // 'PAYMENT' or 'CREDIT_NOTE'
    const [isActive, setIsActive] = useState(true); // Account active status
    const [editTransaction, setEditTransaction] = useState(null); // Transaction being edited
    const [editForm, setEditForm] = useState({ date: '', amount: '', description: '', referenceNumber: '' }); // Edit form state
    const [selectedInvoice, setSelectedInvoice] = useState(null); // For invoice details popup
    
    // NEW STATE: Date and Reference Number for Payments
    const now = new Date();
    // Format to YYYY-MM-DDTHH:mm (required for datetime-local input value)
    const formattedNow = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);

    const [paymentDate, setPaymentDate] = useState(formattedNow); 
    const [referenceNumber, setReferenceNumber] = useState('');
    
    // NEW STATE: Cheque details
    const [paymentMethod, setPaymentMethod] = useState('CASH'); // 'CASH' or 'BANK'
    const [chequeNumber, setChequeNumber] = useState('');

// --- NEW STK PUSH STATES ---
    const [showStkModal, setShowStkModal] = useState(false);
    const [useRegisteredPhone, setUseRegisteredPhone] = useState(true);
    const [customPhoneNumber, setCustomPhoneNumber] = useState('');
    const [saveNewNumber, setSaveNewNumber] = useState(false);

// Accordion States
    const [openSections, setOpenSections] = useState({
        customerDetails: false,
        payments: true,        // Open by default after search
        transactionHistory: false
    });

    // Selected invoices for manual allocation
    const [selectedInvoices, setSelectedInvoices] = useState([]);

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Reset accordion on new search
    useEffect(() => {
        if (customerData) {
            setOpenSections({
                customerDetails: false,
                payments: true,
                transactionHistory: false
            });
        }
    }, [customerData]);



    // Helper to format the balance for display
    const currentBalance = useMemo(() => {
        if (customerData) {
            const balance = parseFloat(customerData.current_balance);
            return {
                value: Math.abs(balance).toFixed(2),
                isCredit: balance < 0,
                label: balance >= 0 ? 'Current Balance Owed' : 'Current Balance Credit'
            };
        }
        return { value: '0.00', isCredit: false, label: 'Current Balance Owed' };
    }, [customerData]);

    // Compute transaction statuses using allocations
    const transactionStatuses = useMemo(() => {
    if (!customerData || !customerData.transactions || !customerData.allocations) {
        return { invoices: [], payments: [], creditNotes: [], ledger: [] };
    }

    // Map transactions for quick lookup, ensuring amount is a number
    const transactionMap = new Map(customerData.transactions.map(tx => [
        tx.transaction_id,
        { ...tx, amount: parseFloat(tx.amount) } // Convert amount to number
    ]));

    // Calculate invoice statuses
    const invoices = customerData.transactions
        .filter(tx => tx.transaction_type === 'INVOICE' && parseFloat(tx.amount) > 0)
        .map(tx => {
            const allocations = customerData.allocations
                .filter(alloc => alloc.invoice_transaction_id === tx.transaction_id)
                .map(alloc => ({
                    payment: transactionMap.get(alloc.payment_transaction_id),
                    amount: parseFloat(alloc.amount) // Convert allocation amount to number
                }));
            const totalAllocated = allocations.reduce((sum, alloc) => sum + alloc.amount, 0);
            const remaining = parseFloat(tx.amount) - totalAllocated;
            const status = remaining === 0 ? 'Fully Paid' : remaining === parseFloat(tx.amount) ? 'Unpaid' : 'Partially Paid';
            return { tx: { ...tx, amount: parseFloat(tx.amount) }, remaining, applied: allocations, status };
        });

    // Calculate payment statuses (applies to both CASH and MPESA)
    const payments = customerData.transactions
        .filter(tx => tx.transaction_type === 'PAYMENT' && parseFloat(tx.amount) < 0)
        .map(tx => {
            const totalUtilized = customerData.allocations
                .filter(alloc => alloc.payment_transaction_id === tx.transaction_id)
                .reduce((sum, alloc) => sum + parseFloat(alloc.amount), 0);
            const absAmount = Math.abs(parseFloat(tx.amount));
            const status = totalUtilized >= absAmount ? 'Fully Utilized' : totalUtilized > 0 ? 'Partially Utilized' : 'Not Utilized';
            return { tx: { ...tx, amount: parseFloat(tx.amount) }, utilized: totalUtilized, status };
        });

    // Calculate credit note statuses
    const creditNotes = customerData.transactions
        .filter(tx => tx.transaction_type === 'CREDIT_NOTE' && parseFloat(tx.amount) < 0)
        .map(tx => {
            const totalUtilized = customerData.allocations
                .filter(alloc => alloc.payment_transaction_id === tx.transaction_id)
                .reduce((sum, alloc) => sum + parseFloat(alloc.amount), 0);
            const absAmount = Math.abs(parseFloat(tx.amount));
            const status = totalUtilized >= absAmount ? 'Fully Utilized' : totalUtilized > 0 ? 'Partially Utilized' : 'Not Utilized';
            return { tx: { ...tx, amount: parseFloat(tx.amount) }, utilized: totalUtilized, status };
        });

    // Build ledger for transaction history display
    const ledger = [
        ...invoices.map(item => ({
            ...item.tx,
            type: 'INVOICE',
            status: item.status,
            remaining: item.remaining
        })),
        ...payments.map(item => ({
            ...item.tx,
            type: 'PAYMENT',
            status: item.status,
            payment_method: item.tx.payment_method || 'Unknown'
        })),
        ...creditNotes.map(item => ({
            ...item.tx,
            type: 'CREDIT_NOTE',
            status: item.status
        }))
    ].sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date));

    return { invoices, payments, creditNotes, ledger };
}, [customerData]);

useEffect(() => {
        if (!amountToPay || parseFloat(amountToPay) <= 0 || !customerData) {
            setSelectedInvoices([]);
            return;
        }

        const amount = parseFloat(amountToPay);
        const unpaidInvoices = transactionStatuses.invoices
            .filter(inv => inv.remaining > 0)
            .sort((a, b) => new Date(a.tx.transaction_date) - new Date(b.tx.transaction_date));

        let remainingAmount = amount;
        const selected = [];

        for (const inv of unpaidInvoices) {
            if (remainingAmount <= 0) break;
            const apply = Math.min(remainingAmount, inv.remaining);
            if (apply > 0) {
                selected.push(inv.tx.transaction_id);
                remainingAmount -= apply;
            }
        }

        setSelectedInvoices(selected);
    }, [amountToPay, transactionStatuses.invoices]);



    // Helper to render status messages using Emojis/Text
    const renderStatus = (status, type) => {
        const baseStyle = {
            padding: '12px',
            borderRadius: '8px',
            fontWeight: '600',
            marginTop: '15px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px',
 notch: 'none'
        };

        let style = {};
        let prefix = '';

        switch (type) {
            case 'success':
                style = { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' };
                prefix = '✅';
                break;
            case 'error':
                style = { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24' };
                prefix = '❌';
                break;
            case 'loading':
                style = { ...baseStyle, backgroundColor: '#cce5ff', color: '#004085' };
                prefix = '🔄';
                break;
            default:
                style = { ...baseStyle, backgroundColor: '#e2e3e5', color: '#383d41' };
                prefix = 'ℹ️';
                break;
        }

        return (
            <div style={style} data-type={type}>
                <span style={{ marginRight: '10px' }}>{prefix}</span>
                {status}
            </div>
        );
    };

    // --- Data Fetching (Search) ---
    const handleSearch = async (e) => {
        e.preventDefault();
        setSearchStatus(renderStatus('Searching for account...', 'loading'));
        setCustomerData(null);
        setAmountToPay('');
        setPaymentStatus(null);
        setEditTransaction(null);
        setSelectedInvoice(null);
        
        // Reset new fields
        setReferenceNumber('');
        setChequeNumber('');
        setPaymentMethod('CASH');
        setTransactionType('PAYMENT');
        setPaymentDate(formattedNow);

        try {
            const response = await axios.get(`${API_BASE_URL}/customer-data`, {
                params: { accountNumber }
            });

            if (response.data) {
                setCustomerData(response.data);
                setIsActive(response.data.is_active);
                setSearchStatus(renderStatus(`Account found for ${response.data.first_name} ${response.data.last_name}`, 'success'));
            } else {
                setSearchStatus(renderStatus(`Account number ${accountNumber} not found.`, 'error'));
            }
        } catch (err) {
            console.error('Search failed:', err);
            setSearchStatus(renderStatus(`Error searching for account: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

    // --- Transaction Submission (Payment or Credit Note) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!customerData || parseFloat(amountToPay) <= 0) {
            setPaymentStatus(renderStatus('Please find a valid account and enter a positive amount.', 'error'));
            return;
        }

        const amount = parseFloat(amountToPay);
        
        // Add validation for date
        if (transactionType === 'PAYMENT' && !paymentDate) {
            setPaymentStatus(renderStatus('Please select a payment date.', 'error'));
            return;
        }

        if (transactionType === 'PAYMENT' && paymentMethod === 'BANK' && !chequeNumber.trim()) {
            setPaymentStatus(renderStatus('Please enter a valid cheque number.', 'error'));
            return;
        }

        setPaymentStatus(renderStatus('Processing transaction...', 'loading'));

        try {
            // Merge bank reference prefix and cheque ID for structured ref logs
            let dynamicRef = referenceNumber.trim();
            if (paymentMethod === 'BANK') {
                dynamicRef = referenceNumber.trim() 
                    ? `CHEQUE-${chequeNumber.trim()} / ${referenceNumber.trim()}`
                    : `CHEQUE-${chequeNumber.trim()}`;
            }

            const payload = {
                accountNumber: customerData.account_number,
                amount: amount,
                transactionType: transactionType,
                paymentMethod: transactionType === 'PAYMENT' ? paymentMethod : null,
                referenceNumber: transactionType === 'PAYMENT' ? dynamicRef || null : null,
                transactionDate: transactionType === 'PAYMENT' ? paymentDate : new Date().toISOString(),
                selectedInvoices: selectedInvoices.length > 0 ? selectedInvoices : undefined
            };

            const response = await axios.post(`${API_BASE_URL}/record-payment`, payload);

            setPaymentStatus(renderStatus(response.data.message, 'success'));
            setAmountToPay('');
            // Reset new fields
            setReferenceNumber('');
            setChequeNumber('');
            setPaymentMethod('CASH');
            setTransactionType('PAYMENT');
            setPaymentDate(formattedNow);
            await handleSearch(new Event('submit', { cancelable: true })); 
        } catch (err) {
            console.error('Transaction submission failed:', err);
            setPaymentStatus(renderStatus(`Transaction failed: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

// --- STK PUSH API HANDLER ---

const handleSendStk = async () => {
        let phoneToSend = customerData.phone_number;
        
        if (!customerData.phone_number || !useRegisteredPhone) {
            phoneToSend = customPhoneNumber;
            if (!phoneToSend || phoneToSend.trim() === '') {
                setPaymentStatus(renderStatus('Please enter a valid phone number.', 'error'));
                setShowStkModal(false);
                return;
            }
        }

        setPaymentStatus(renderStatus('Sending STK Push...', 'loading'));
        setShowStkModal(false); 

        try {
            const payload = {
                accountNumber: customerData.account_number,
                amount: parseFloat(amountToPay),
                phoneNumber: phoneToSend,
                updateAccountPhone: (!customerData.phone_number || !useRegisteredPhone) ? saveNewNumber : false
            };

            await axios.post(`${API_BASE_URL}/initiate-stk`, payload);
            setPaymentStatus(renderStatus(`STK Push sent successfully to ${phoneToSend}. Awaiting payment...`, 'success'));
            
            setCustomPhoneNumber('');
            setSaveNewNumber(false);
            setUseRegisteredPhone(true);
        } catch (err) {
            setPaymentStatus(renderStatus(`STK failed: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

    // --- Delete Customer ---
    const handleDeleteCustomer = async () => {
        if (!customerData) return;
        if (!window.confirm(`Are you sure you want to delete customer ${customerData.first_name} ${customerData.last_name} and their account ${customerData.account_number}?`)) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/delete-customer`, {
                params: { userId: customerData.user_id }
            });
            setSearchStatus(renderStatus('Customer deleted successfully.', 'success'));
            setCustomerData(null);
            setAccountNumber('');
            setIsActive(true);
            setEditTransaction(null);
            setSelectedInvoice(null);
        } catch (err) {
            console.error('Delete customer failed:', err);
            setSearchStatus(renderStatus(`Error deleting customer: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

    // --- Update Account Status ---
    const handleStatusChange = async (e) => {
        const newStatus = e.target.value === 'active';
        setIsActive(newStatus);

        try {
            await axios.put(`${API_BASE_URL}/update-account-status`, {
                accountNumber: customerData.account_number,
                isActive: newStatus
            });
            setSearchStatus(renderStatus(`Account status updated to ${newStatus ? 'Active' : 'Inactive'}.`, 'success'));
            await handleSearch(new Event('submit', { cancelable: true })); 
        } catch (err) {
            console.error('Status update failed:', err);
            setSearchStatus(renderStatus(`Error updating status: ${err.response?.data?.error || err.message}`, 'error'));
            setIsActive(customerData.is_active); // Revert on error
        }
    };

    // --- Print Transaction History ---
    const handlePrint = () => {
        const printContent = document.getElementById('transaction-history').outerHTML;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Transaction History for ${customerData.account_number}</title>
                    <style>
                        body { font-family: Inter, sans-serif; }
                        table { width: 100%; border-collapse: collapse; }
                        th, td { padding: 12px 15px; border: 1px solid #ddd; text-align: left; }
                        th { background-color: #f1f1f1; }
                        .credit { background-color: #e8f5e9; }
                        .debit { background-color: #fff0f0; }
                    </style>
                </head>
                <body>
                    <h2>Transaction History for ${customerData.first_name} ${customerData.last_name} (${customerData.account_number})</h2>
                    ${printContent}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // --- Edit Transaction ---
    const openEditModal = (transaction) => {
        setEditTransaction(transaction);
        setEditForm({
            // Ensure the date is in the YYYY-MM-DDTHH:mm format for datetime-local
            date: new Date(transaction.transaction_date).toISOString().slice(0, 16),
            amount: Math.abs(parseFloat(transaction.amount)).toFixed(2),
            description: transaction.description,
            referenceNumber: transaction.reference_number || ''
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editTransaction) return;

        try {
            const payload = {
                transactionId: editTransaction.transaction_id,
                transactionDate: editForm.date,
                amount: parseFloat(editForm.amount) * (editTransaction.amount < 0 ? -1 : 1), // Preserve sign
                transactionType: editForm.description,
                referenceNumber: editForm.referenceNumber || null
            };

            await axios.put(`${API_BASE_URL}/edit-transaction`, payload);
            setSearchStatus(renderStatus('Transaction updated successfully.', 'success'));
            setEditTransaction(null);
            await handleSearch(new Event('submit', { cancelable: true })); 
        } catch (err) {
            console.error('Edit transaction failed:', err);
            setSearchStatus(renderStatus(`Error updating transaction: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

    // --- Delete Transaction ---
    const handleDeleteTransaction = async (transactionId) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/delete-transaction`, {
                params: { transactionId }
            });
            setSearchStatus(renderStatus('Transaction deleted successfully.', 'success'));
            setSelectedInvoice(null); // Close popup if open
            await handleSearch(new Event('submit', { cancelable: true })); 
        } catch (err) {
            console.error('Delete transaction failed:', err);
            setSearchStatus(renderStatus(`Error deleting transaction: ${err.response?.data?.error || err.message}`, 'error'));
        }
    };

    // Helper function to get status icon
    const getStatusIcon = (tx) => {
        if (tx.transaction_type === 'INVOICE') {
            const invoice = transactionStatuses.invoices.find(inv => inv.tx.transaction_id === tx.transaction_id);
            if (!invoice) return '❌'; // Fallback if invoice not found
            const status = invoice.remaining === 0 ? '✅' : invoice.remaining === parseFloat(tx.amount) ? '❌' : '⚠️';
            return (
                <span
                    style={{ cursor: 'pointer', color: status === '✅' ? '#28a745' : status === '⚠️' ? '#ffc107' : '#dc3545' }}
                    onClick={() => setSelectedInvoice(invoice)}
                >
                    {status}
                </span>
            );
        } else if (tx.transaction_type === 'PAYMENT') {
            const payment = transactionStatuses.payments.find(p => p.tx.transaction_id === tx.transaction_id);
            if (!payment) return '❌';
            const status = payment.utilized === -parseFloat(tx.amount) ? '✅' : '❌';
            return <span style={{ color: status === '✅' ? '#28a745' : '#dc3545' }}>{status}</span>;
        } else if (tx.transaction_type === 'CREDIT_NOTE') {
            return <span style={{ color: '#28a745' }}>✅</span>;
        }
        return '';
    };

    // Helper function to render a single transaction row
    const renderTransactionRow = (transaction) => {
        const isCredit = parseFloat(transaction.amount) < 0;
        const formattedAmount = Math.abs(parseFloat(transaction.amount)).toFixed(2);
        const date = new Date(transaction.transaction_date).toLocaleDateString('en-KE', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });

        return (
            <tr key={transaction.transaction_id} style={{ ...styles.tableRow, backgroundColor: isCredit ? '#e8f5e9' : '#fff0f0' }} className={isCredit ? 'credit' : 'debit'}>
                <td style={styles.tableCell}>{date}</td>
                <td style={styles.tableCell}>
                    <span style={{ fontWeight: 'bold', color: isCredit ? '#1b5e20' : '#b71c1c' }}>
                        {isCredit ? 'CREDIT' : 'DEBIT'}
                    </span>
                </td>
                <td style={styles.tableCell}>
                    {transaction.description || transaction.transaction_type}
                    {transaction.payment_method && ` (${transaction.payment_method})`}
                </td>
                <td style={styles.tableCell}>{transaction.reference_number || '-'}</td>
                <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold', color: isCredit ? '#1b5e20' : '#b71c1c' }}>
                    Ksh {formattedAmount}
                </td>
                <td style={styles.tableCell}>{getStatusIcon(transaction)}</td>
                <td style={styles.tableCell}>
                    <button
                        style={{ ...styles.actionButton, backgroundColor: '#007bff' }}
                        onClick={() => openEditModal(transaction)}
                    >
                        ✏️ Edit
                    </button>
                    <button
                        style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
                        onClick={() => handleDeleteTransaction(transaction.transaction_id)}
                    >
                        🗑️ Delete
                    </button>
                </td>
            </tr>
        );
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.header}>Record Customer Transaction (3.3)</h2>

            <form onSubmit={handleSearch} style={styles.form}>
                <div style={styles.inputGroup}>
                    <label htmlFor="accountNumber" style={styles.label}>Account Number *</label>
                    <input
                        id="accountNumber"
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        placeholder="e.g., KGW001"
                        required
                        style={styles.input}
                    />
                </div>
                <button type="submit" style={styles.searchButton}>🔍 Search Account</button>
            </form>

            {searchStatus}

            {customerData && (
                <div style={{ marginTop: '30px' }}>

                    <div style={styles.accordionHeader} onClick={() => toggleSection('customerDetails')}>
                        <h3 style={{ margin: 0, color: '#007bff' }}>Customer Details {openSections.customerDetails ? '−' : '+'}</h3>
                    </div>
                    {openSections.customerDetails && (
                        <div style={styles.accordionContent}>
                            <div style={styles.infoGrid}>
                                <p><strong>Name:</strong> {customerData.first_name} {customerData.last_name}</p>
                                <p><strong>Phone:</strong> {customerData.phone_number}</p>
                                <p><strong>Address:</strong> {customerData.installation_address}</p>
                                <p><strong>Block:</strong> {customerData.block_name}</p>
                                <p><strong>Account No:</strong> {customerData.account_number}</p>
                                <div style={{
                                    ...styles.balanceDisplay,
                                    backgroundColor: currentBalance.isCredit ? '#e8f5e9' : '#fff0f0',
                                    borderColor: currentBalance.isCredit ? '#c3e6cb' : '#ffcccc'
                                }}>
                                    {currentBalance.label}: Ksh {currentBalance.value}
                                </div>
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Account Status</label>
                                <div style={styles.radioGroup}>
                                    <label><input type="radio" value="active" checked={isActive} onChange={handleStatusChange} /> Active</label>
                                    <label style={{ marginLeft: '20px' }}><input type="radio" value="inactive" checked={!isActive} onChange={handleStatusChange} /> Inactive</label>
                                </div>
                            </div>

                            {!isActive && (
                                <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#fff3cd', color: '#856404', marginBottom: '20px' }}>
                                    <span style={{ marginRight: '10px' }}>Info</span>
                                    This account is inactive. Upon reactivation, a reconnection fee of Ksh 500 will be applied.
                                </div>
                            )}

                            <button style={{ ...styles.submitButton, backgroundColor: '#dc3545' }} onClick={handleDeleteCustomer}>
                                Delete Customer
                            </button>
                        </div>
                    )}


                    <div style={styles.accordionHeader} onClick={() => toggleSection('payments')}>
                        <h3 style={{ margin: 0, color: '#007bff' }}>Payment {openSections.payments ? '−' : '+'}</h3>
                    </div>
                    {openSections.payments && (
                        <div style={styles.accordionContent}>
                            <div style={styles.toggleGroup}>
                                <button 
                                    type="button"
                                    onClick={() => { setTransactionType('PAYMENT'); setPaymentMethod('CASH'); }} 
                                    style={transactionType === 'PAYMENT' && paymentMethod === 'CASH' ? styles.activeToggle : styles.toggleButton}
                                >
                                    Cash Payment
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => { setTransactionType('PAYMENT'); setPaymentMethod('BANK'); }} 
                                    style={transactionType === 'PAYMENT' && paymentMethod === 'BANK' ? styles.activeToggle : styles.toggleButton}
                                >
                                    Cheque Payment
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => { setTransactionType('CREDIT_NOTE'); setPaymentMethod('CASH'); }} 
                                    style={transactionType === 'CREDIT_NOTE' ? styles.activeToggle : styles.toggleButton}
                                >
                                    Credit Note
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                {transactionType === 'PAYMENT' && (
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="paymentDate" style={styles.label}>Payment Date and Time *</label>
                                        <input id="paymentDate" type="datetime-local" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required style={styles.input} />
                                    </div>
                                )}

                                <div style={styles.inputGroup}>
                                    <label htmlFor="amountToPay" style={styles.label}>Amount (Ksh) *</label>
                                    <input
                                        id="amountToPay"
                                        type="number"
                                        value={amountToPay}
                                        onChange={(e) => setAmountToPay(e.target.value)}
                                        placeholder="e.g., 500.00"
                                        min="0.01"
                                        step="0.01"
                                        required
                                        style={styles.input}
                                    />
                                </div>

                                {transactionType === 'PAYMENT' && paymentMethod === 'BANK' && (
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="chequeNumber" style={styles.label}>Cheque Number *</label>
                                        <input 
                                            id="chequeNumber" 
                                            type="text" 
                                            value={chequeNumber} 
                                            onChange={(e) => setChequeNumber(e.target.value)} 
                                            placeholder="e.g., CHQ98765" 
                                            required 
                                            style={styles.input} 
                                        />
                                    </div>
                                )}

                                {transactionType === 'PAYMENT' && (
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="referenceNumber" style={styles.label}>Reference Number (Optional)</label>
                                        <input id="referenceNumber" type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="e.g., RECEIPT12345" style={styles.input} />
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button type="submit" style={{ ...styles.submitButton, marginTop: '0', flex: 1 }}>
                                        {transactionType === 'CREDIT_NOTE' ? 'Submit Credit Note' : paymentMethod === 'BANK' ? 'Submit Cheque Payment' : 'Submit Cash Payment'}
                                    </button>
                                    
                                    {transactionType === 'PAYMENT' && paymentMethod === 'CASH' && (
                                        <button 
                                            type="button" 
                                            style={{ ...styles.submitButton, backgroundColor: '#25D366', marginTop: '0', flex: 1 }}
                                            onClick={() => {
                                                if (!amountToPay || parseFloat(amountToPay) <= 0) {
                                                    setPaymentStatus(renderStatus('Please enter a valid amount before sending STK.', 'error'));
                                                    return;
                                                }
                                                setShowStkModal(true);
                                            }}
                                        >
                                            📱 Send M-Pesa STK
                                        </button>
                                    )}
                                </div>
                                {paymentStatus}
                            </form>

                            {transactionStatuses.invoices.some(inv => inv.remaining > 0) ? (
                                <div style={{ marginTop: '25px' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Unpaid Invoices (Auto-selected by FIFO)</h4>
                                    <div style={styles.tableWrapper}>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr style={styles.tableHeaderRow}>
                                                    <th style={styles.tableHeader}>Select</th>
                                                    <th style={styles.tableHeader}>Date</th>
                                                    <th style={styles.tableHeader}>Invoice Amount</th>
                                                    <th style={styles.tableHeader}>Balance Due</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {transactionStatuses.invoices
                                                    .filter(inv => inv.remaining > 0)
                                                    .sort((a, b) => new Date(a.tx.transaction_date) - new Date(b.tx.transaction_date))
                                                    .map(inv => {
                                                        const isChecked = selectedInvoices.includes(inv.tx.transaction_id);
                                                        return (
                                                            <tr key={inv.tx.transaction_id}>
                                                                <td style={styles.tableCell}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedInvoices(prev => [...prev, inv.tx.transaction_id]);
                                                                            } else {
                                                                                setSelectedInvoices(prev => prev.filter(id => id !== inv.tx.transaction_id));
                                                                            }
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td style={styles.tableCell}>
                                                                    {new Date(inv.tx.transaction_date).toLocaleDateString('en-KE')}
                                                                </td>
                                                                <td style={styles.tableCell}>Ksh {parseFloat(inv.tx.amount).toFixed(2)}</td>
                                                                <td style={styles.tableCell}>Ksh {inv.remaining.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3" style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold' }}>Total Due:</td>
                                                    <td style={{ ...styles.tableCell, fontWeight: 'bold' }}>
                                                        Ksh {transactionStatuses.invoices
                                                            .filter(inv => inv.remaining > 0)
                                                            .reduce((sum, inv) => sum + inv.remaining, 0)
                                                            .toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ color: '#28a745', fontWeight: 'bold', marginTop: '20px' }}>No unpaid invoices.</p>
                            )}
                        </div>
                    )}

                                        {/* Transaction History */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '40px' }}>
                        <h3 style={{ 
                            margin: 0, 
                            color: '#007bff', 
                            fontSize: '1.6em', 
                            fontWeight: '700' 
                        }}>
                            Transaction History 
                            <span style={{ fontSize: '0.9em', color: '#666', fontWeight: 'normal' }}>
                                ({customerData.transactions?.length || 0} entries)
                            </span>
                        </h3>
                        <button
                            style={{ 
                                ...styles.submitButton, 
                                backgroundColor: '#17a2b8',
                                padding: '10px 20px',
                                fontSize: '15px'
                            }}
                            onClick={handlePrint}
                        >
                            Print History
                        </button>
                    </div>

                    {customerData.transactions && customerData.transactions.length > 0 ? (
                        <div style={styles.tableWrapper}>
                            <table style={styles.table} id="transaction-history">
                                <thead>
                                    <tr style={styles.tableHeaderRow}>
                                        <th style={styles.tableHeader}>Date & Time</th>
                                        <th style={styles.tableHeader}>Type</th>
                                        <th style={styles.tableHeader}>Description</th>
                                        <th style={styles.tableHeader}>Ref No.</th>
                                        <th style={{...styles.tableHeader, textAlign: 'right'}}>Amount (Ksh)</th>
                                        <th style={styles.tableHeader}>Status</th>
                                        <th style={styles.tableHeader}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...customerData.transactions]
                                        .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))
                                        .map(renderTransactionRow)}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            color: '#666',
                            fontStyle: 'italic'
                        }}>
                            No transaction history found for this account.
                        </div>
                    )}

{/* M-Pesa STK Modal */}
                    {showStkModal && (
                        <div style={styles.modalOverlay}>
                            <div style={styles.modal}>
                                <h3 style={styles.modalHeader}>Confirm M-Pesa STK Push</h3>
                                <p><strong>Amount:</strong> Ksh {parseFloat(amountToPay).toFixed(2)}</p>

                                {customerData.phone_number ? (
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Which number should receive the prompt?</label>
                                        <div style={styles.radioGroup}>
                                            <label><input type="radio" checked={useRegisteredPhone} onChange={() => setUseRegisteredPhone(true)} /> Registered ({customerData.phone_number})</label>
                                            <label style={{ marginLeft: '15px' }}><input type="radio" checked={!useRegisteredPhone} onChange={() => setUseRegisteredPhone(false)} /> Different Number</label>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ padding: '10px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px', marginBottom: '15px' }}>No phone number is registered for this account.</div>
                                )}

                                {(!customerData.phone_number || !useRegisteredPhone) && (
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="customPhoneNumber" style={styles.label}>Enter M-Pesa Number (e.g., 2547...)</label>
                                        <input id="customPhoneNumber" type="text" value={customPhoneNumber} onChange={(e) => setCustomPhoneNumber(e.target.value)} placeholder="2547XXXXXXXX" style={styles.input} />
                                        <label style={{ display: 'flex', alignItems: 'center', marginTop: '10px', cursor: 'pointer', fontSize: '0.9em' }}>
                                            <input type="checkbox" checked={saveNewNumber} onChange={(e) => setSaveNewNumber(e.target.checked)} style={{ marginRight: '8px' }} />
                                            Save this number to the account for future use
                                        </label>
                                    </div>
                                )}

                                <div style={styles.modalButtons}>
                                    <button type="button" style={{ ...styles.submitButton, backgroundColor: '#25D366' }} onClick={handleSendStk}>Send Prompt</button>
                                    <button type="button" style={{ ...styles.submitButton, backgroundColor: '#6c757d' }} onClick={() => setShowStkModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Edit Transaction Modal - MUST be inside the main div */}
                    {editTransaction && (
                        <div style={styles.modalOverlay}>
                            <div style={styles.modal}>
                                <h3 style={styles.modalHeader}>Edit Transaction</h3>
                                <form onSubmit={handleEditSubmit}>
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="editDate" style={styles.label}>Date *</label>
                                        <input
                                            id="editDate"
                                            type="datetime-local"
                                            value={editForm.date}
                                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                            required
                                            style={styles.input}
                                        />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="editAmount" style={styles.label}>Amount (Ksh) *</label>
                                        <input
                                            id="editAmount"
                                            type="number"
                                            value={editForm.amount}
                                            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                            min="0.01"
                                            step="0.01"
                                            required
                                            style={styles.input}
                                        />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="editDescription" style={styles.label}>Description *</label>
                                        <select
                                            id="editDescription"
                                            value={editForm.description}
                                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                            required
                                            style={styles.input}
                                        >
                                            <option value="INVOICE">INVOICE</option>
                                            <option value="PAYMENT">PAYMENT</option>
                                            <option value="CREDIT_NOTE">CREDIT_NOTE</option>
                                        </select>
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label htmlFor="editReferenceNumber" style={styles.label}>Reference Number</label>
                                        <input
                                            id="editReferenceNumber"
                                            type="text"
                                            value={editForm.referenceNumber}
                                            onChange={(e) => setEditForm({ ...editForm, referenceNumber: e.target.value })}
                                            style={styles.input}
                                        />
                                    </div>
                                    <div style={styles.modalButtons}>
                                        <button type="submit" style={{ ...styles.submitButton, backgroundColor: '#28a745' }}>
                                            Save Changes
                                        </button>
                                        <button
                                            type="button"
                                            style={{ ...styles.submitButton, backgroundColor: '#6c757d' }}
                                            onClick={() => setEditTransaction(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Invoice Details Popup - MUST also be inside */}
                    {selectedInvoice && selectedInvoice.tx && (
                        <div style={styles.modalOverlay}>
                            <div style={styles.modal}>
                                <h3 style={styles.modalHeader}>Invoice Details</h3>
                                <p><strong>Invoice Date:</strong> {new Date(selectedInvoice.tx.transaction_date).toLocaleString()}</p>
                                <p><strong>Original Amount:</strong> Ksh {(parseFloat(selectedInvoice.tx.amount) || 0).toFixed(2)}</p>
                                <p><strong>Remaining:</strong> Ksh {(parseFloat(selectedInvoice.remaining) || 0).toFixed(2)}</p>
                                <h4>Applied Payments:</h4>
                                {selectedInvoice.applied.length > 0 ? (
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>Ref No.</th>
                                                <th>Applied Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedInvoice.applied.map((app, index) => (
                                                <tr key={index}>
                                                    <td>{app.payment ? new Date(app.payment.transaction_date).toLocaleString() : '-'}</td>
                                                    <td>{app.payment?.transaction_type || '-'}</td>
                                                    <td>{app.payment?.reference_number || '-'}</td>
                                                    <td>Ksh {(parseFloat(app.amount) || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p>No payments applied yet.</p>
                                )}
                                <button
                                    style={{ ...styles.submitButton, backgroundColor: '#6c757d' }}
                                    onClick={() => setSelectedInvoice(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}

                </div>  
            )}  

        </div>  
    );
}


const styles = {
    container: {
        maxWidth: '900px',
        margin: '40px auto',
        padding: '30px',
        backgroundColor: '#f9f9f9',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Inter, sans-serif',
    },
    header: {
        textAlign: 'center',
        color: '#007bff',
        marginBottom: '25px',
        fontSize: '1.8em',
        borderBottom: '2px solid #eef2f5',
        paddingBottom: '10px',
    },
    form: {
        marginBottom: '20px',
    },
    inputGroup: {
        marginBottom: '15px',
    },
    label: {
        display: 'block',
        marginBottom: '5px',
        fontWeight: '600',
        color: '#333',
    },
    input: {
        width: '100%',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '6px',
        boxSizing: 'border-box',
        fontSize: '16px',
        transition: 'border-color 0.3s',
    },
 accordionHeader : {
    backgroundColor: '#f1f3f5',
    padding: '15px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '20px',
    border: '1px solid #ddd',
    userSelect: 'none'
},

accordionContent : {
    border: '1px solid #ddd',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    padding: '20px',
    backgroundColor: '#fff'
},

    searchButton: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '16px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    submitButton: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '16px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
        marginTop: '15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    actionButton: {
        padding: '8px 12px',
        marginRight: '10px',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    detailsContainer: {
        border: '1px solid #ddd',
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: '#fff',
        marginTop: '20px',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    },
    detailsHeader: {
        color: '#333',
        borderBottom: '1px solid #eee',
        paddingBottom: '10px',
        marginBottom: '15px',
        fontSize: '1.4em',
        fontWeight: '700',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '15px',
        fontSize: '1em',
        marginBottom: '25px',
        padding: '10px 0',
        borderBottom: '1px solid #eee',
    },
    balanceDisplay: {
        gridColumn: 'span 3',
        textAlign: 'center',
        padding: '15px',
        borderRadius: '6px',
        fontWeight: 'bold',
        fontSize: '1.2em',
    },
    toggleGroup: {
        display: 'flex',
        marginBottom: '20px',
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid #007bff',
    },
    toggleButton: {
        flexGrow: '1',
        padding: '10px 15px',
        border: 'none',
        backgroundColor: '#fff',
        color: '#007bff',
        cursor: 'pointer',
        transition: 'all 0.3s',
        fontWeight: '500',
    },
    activeToggle: {
        flexGrow: '1',
        padding: '10px 15px',
        border: 'none',
        backgroundColor: '#007bff',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    radioGroup: {
        display: 'flex',
        alignItems: 'center',
    },
    historyContainer: {
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid #eee',
    },
    historyHeader: {
        fontSize: '1.4em',
        fontWeight: '700',
        color: '#333',
        marginBottom: '15px',
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: '8px',
        border: '1px solid #ddd',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.95em',
    },
    tableHeaderRow: {
        backgroundColor: '#f1f1f1',
    },
    tableHeader: {
        padding: '12px 15px',
        textAlign: 'left',
        fontWeight: '700',
        color: '#555',
        borderBottom: '2px solid #ddd',
    },
    tableCell: {
        padding: '12px 15px',
        borderBottom: '1px solid #eee',
        verticalAlign: 'top',
    },
    tableRow: {
        transition: 'background-color 0.2s',
    },
    noHistory: {
        textAlign: 'center',
        padding: '20px',
        color: '#777',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        width: '400px',
        maxWidth: '90%',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
    modalHeader: {
        fontSize: '1.2em',
        fontWeight: '700',
        marginBottom: '15px',
    },
    modalButtons: {
        display: 'flex',
        gap: '10px',
    }
};

export default RecordPaymentForm;